import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Link2, Loader2, Mail, MailX, Search, Send, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { errorMessage, getInvitations, previewInvitation, sendInvitations } from "@/lib/api";
import { currentLanguage, t, tf } from "@/i18n";

/* Who has been asked, who has answered, and the button that asks.

   Each guest's link is derived on the server from the wedding and guest ids, so
   it is never stored and never stale. Sending is one click for everyone with
   an address who has not answered; the reminders after that are the server's
   job, not the couple's memory. */

const Metric = ({ label, value, tone }) => (
  <div className="rounded-lg border p-3.5" style={{ borderColor: "var(--line)" }}>
    <p className="text-[10.5px] uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">{label}</p>
    <p className="font-num mt-1 text-[24px] leading-none" style={tone ? { color: tone } : undefined}>
      {value}
    </p>
  </div>
);

const FILTERS = [
  { id: "all", label: "invitations.all" },
  { id: "silent", label: "invitations.filterSilent" },
  { id: "answered", label: "invitations.filterAnswered" },
  { id: "no_email", label: "invitations.noEmail" },
];

const shortDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(currentLanguage() === "en" ? "en-GB" : "fr-FR", {
    day: "numeric",
    month: "short",
  });
};

export const InvitationsTab = ({ weddingId, contactEmail, onGoToOverview }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const load = () =>
    getInvitations(weddingId, window.location.origin)
      .then(setData)
      .catch((error) => toast.error(errorMessage(error, t("invitations.cannotLoad"))))
      .finally(() => setLoading(false));

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weddingId]);

  const rows = useMemo(() => {
    const all = data?.guests || [];
    const needle = search.trim().toLowerCase();
    return all.filter((row) => {
      if (needle && !`${row.name} ${row.email}`.toLowerCase().includes(needle)) return false;
      if (filter === "silent") return !row.answered;
      if (filter === "answered") return row.answered;
      if (filter === "no_email") return !row.email;
      return true;
    });
  }, [data, filter, search]);

  const copy = async (row) => {
    try {
      await navigator.clipboard.writeText(row.url);
      setCopied(row.id);
      setTimeout(() => setCopied(""), 1600);
    } catch {
      toast.message(row.url);
    }
  };

  const copyAll = async () => {
    const lines = rows
      .filter((row) => row.email)
      .map((row) => `${row.name}\t${row.email}\t${row.url}`)
      .join("\n");
    if (!lines) {
      toast.error(t("invitations.noEmailSelection"));
      return;
    }
    try {
      await navigator.clipboard.writeText(`Nom\tCourriel\tLien personnel\n${lines}`);
      toast.success(tf("invitations.rowsCopied", { n: rows.filter((r) => r.email).length }));
    } catch {
      toast.error(t("invitations.copyFailed"));
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const result = await sendInvitations(weddingId);
      if (result.failed?.length) {
        toast.warning(tf("invitations.sentPartial", { n: result.sent, f: result.failed.length }), {
          description: result.failed.map((f) => `${f.name}: ${f.error}`).join(" · "),
          duration: 12000,
        });
      } else {
        toast.success(tf("invitations.sent", { n: result.sent }));
      }
      await load();
    } catch (error) {
      toast.error(errorMessage(error, t("invitations.sendFailed")));
    } finally {
      setSending(false);
      setConfirm(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const result = await previewInvitation(weddingId);
      toast.success(tf("invitations.previewSent", { email: result.sent_to }));
    } catch (error) {
      toast.error(errorMessage(error, t("invitations.sendFailed")));
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <p className="flex items-center gap-2 py-8 text-[13.5px] text-[color:var(--muted-ink)]">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("invitations.loading")}
      </p>
    );
  }
  if (!data) return null;

  const { counts } = data;
  const toSend = (data.guests || []).filter((row) => row.email && !row.answered && !row.invited_at).length;
  const canSend = data.mail_configured && Boolean(contactEmail || data.contact_email);

  return (
    <div className="mt-6" data-testid="invitations-tab">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric label={t("invitations.guests")} value={counts.total} />
        <Metric label={t("invitations.withEmail")} value={counts.with_email} />
        <Metric
          label={t("invitations.answered")}
          value={counts.answered}
          tone={counts.answered ? "var(--success)" : undefined}
        />
        {/* Everyone who has not answered, whether or not we can email them —
            the filter below shows the same set, and one screen must not report
            two different numbers for the same thing. */}
        <Metric
          label={t("invitations.silent")}
          value={counts.total - counts.answered}
          tone={counts.total - counts.answered ? "var(--warning)" : undefined}
        />
      </div>

      <section
        className="surface mt-5 flex flex-wrap items-center justify-between gap-4 rounded-xl p-5"
        data-testid="send-panel"
      >
        <div className="min-w-0 max-w-xl">
          <p className="eyebrow">{t("invitations.send")}</p>
          <p className="mt-1 text-[13px] leading-relaxed text-[color:var(--ink-soft)]">
            {t("invitations.remindersOn")}{" "}
            <span className="text-[color:var(--muted-ink)]">{t("invitations.remindersSleep")}</span>
          </p>
          {!data.mail_configured ? (
            <p className="mt-2 text-[12.5px]" style={{ color: "var(--warning)" }} data-testid="mail-not-configured">
              {t("invitations.notConfigured")}
            </p>
          ) : !(contactEmail || data.contact_email) ? (
            <p className="mt-2 text-[12.5px]" style={{ color: "var(--warning)" }} data-testid="need-contact">
              {t("invitations.needContact")}{" "}
              <button type="button" className="link-underline" onClick={onGoToOverview}>
                {t("nav.overview")} →
              </button>
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!canSend || previewing}
            onClick={preview}
            data-testid="preview-invitation"
          >
            {previewing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Eye className="mr-1.5 h-3.5 w-3.5" />}
            {t("invitations.preview")}
          </Button>
          <Button
            disabled={!canSend || sending || !toSend}
            onClick={() => setConfirm(true)}
            data-testid="send-invitations"
          >
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            {toSend
              ? tf("invitations.sendCount", { n: toSend })
              : counts.with_email
                ? t("invitations.nothingToSend")
                : t("invitations.noAddressesYet")}
          </Button>
        </div>
      </section>

      {counts.with_email < counts.total ? (
        <p className="mt-4 text-[12.5px] leading-relaxed" style={{ color: "var(--warning)" }}>
          {tf("invitations.missingAddresses", {
            n: counts.total - counts.with_email,
            m: counts.total - counts.answered - counts.silent,
          })}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {FILTERS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setFilter(entry.id)}
            aria-pressed={filter === entry.id}
            data-testid={`invitations-filter-${entry.id}`}
            className="rounded-full px-3 py-1.5 text-[12.5px]"
            style={{
              backgroundColor: filter === entry.id ? "var(--ink)" : "var(--paper-2)",
              color: filter === entry.id ? "var(--paper)" : "var(--ink-soft)",
            }}
          >
            {t(entry.label)}
          </button>
        ))}

        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5" style={{ color: "var(--muted-ink)" }} />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("invitations.search")}
            data-testid="invitations-search"
            className="h-9 w-44 pl-8 text-[13px]"
          />
        </div>

        <Button variant="outline" size="sm" onClick={copyAll} data-testid="invitations-copy-all">
          <Copy className="mr-1.5 h-3.5 w-3.5" /> {t("invitations.copySelection")}
        </Button>
      </div>

      <div className="surface mt-4 overflow-hidden rounded-xl">
        <ul className="divide-y" style={{ borderColor: "var(--line)" }}>
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center gap-3 px-4 py-3"
              data-testid={`invitation-${row.id}`}
            >
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                style={{
                  backgroundColor: row.answered ? "#eef5f1" : "var(--paper-2)",
                  color: row.answered ? "var(--success)" : "var(--muted-ink)",
                }}
                title={row.answered ? t("invitations.hasAnswered") : t("invitations.notAnswered")}
              >
                {row.answered ? <Check className="h-3.5 w-3.5" /> : <span className="text-[11px]">·</span>}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px]">{row.name}</span>
                <span className="font-num flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[color:var(--muted-ink)]">
                  {row.email ? (
                    <span className="inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {row.email}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <MailX className="h-3 w-3" /> {t("invitations.noAddress")}
                    </span>
                  )}
                  {row.invited_at ? (
                    <span data-testid={`invited-${row.id}`}>
                      · {tf("invitations.invitedOn", { date: shortDate(row.invited_at) })}
                    </span>
                  ) : null}
                  {row.reminders_sent ? <span>· {tf("invitations.reminded", { n: row.reminders_sent })}</span> : null}
                  {row.invited_at && !row.answered ? (
                    <span>
                      ·{" "}
                      {row.next_reminder_at
                        ? tf("invitations.nextReminder", { date: shortDate(row.next_reminder_at) })
                        : t("invitations.noMoreReminders")}
                    </span>
                  ) : null}
                </span>
              </span>

              <a
                href={row.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border p-2"
                style={{ borderColor: "var(--line)" }}
                title={t("invitations.openAsGuest")}
                data-testid={`invitation-open-${row.id}`}
              >
                <Link2 className="h-3.5 w-3.5" style={{ color: "var(--ink-soft)" }} />
              </a>
              <button
                type="button"
                onClick={() => copy(row)}
                className="rounded-full border p-2"
                style={{ borderColor: "var(--line)" }}
                title={t("invitations.copyLink")}
                data-testid={`invitation-copy-${row.id}`}
              >
                {copied === row.id ? (
                  <Check className="h-3.5 w-3.5" style={{ color: "var(--success)" }} />
                ) : (
                  <Copy className="h-3.5 w-3.5" style={{ color: "var(--ink-soft)" }} />
                )}
              </button>
            </li>
          ))}
        </ul>

        {!rows.length ? (
          <p className="px-4 py-8 text-center text-[13px] text-[color:var(--muted-ink)]">{t("invitations.nobody")}</p>
        ) : null}
      </div>

      <p className="mt-4 text-[11.5px] leading-relaxed text-[color:var(--muted-ink)]">{t("invitations.personalHint")}</p>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="send-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">
              {tf("invitations.sendTitle", { n: toSend })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("invitations.sendHint")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="send-cancel">{t("invitations.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="send-confirm" onClick={send} disabled={sending}>
              {t("invitations.sendConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvitationsTab;
